import {
    Backdrop,
    Box,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormHelperText,
    InputAdornment
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import CustomAutocomplete from "../../../shared/AutoComplete";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import CustomTextField from "../../../shared/CustomTextFeild";
import { RootState } from "../../../store/store";
import { formatCurrency } from "../../core/Util";
import basestyle from "../../CSS/Base.module.css";
import { default as CancelPopUp } from "../../PopUp/PopUp";
import { getCreditLinePandIAmountbyCreditId, getCreditlineTransactionDate } from "../services/CreditLine/CreditLineServices";
import { getAllFundSource, getFundTransactionDate } from "../services/Funds/FundService";
import { saveCreditlineTransactionManualEntries, saveFundTransactionManualEntries, saveInvoiceTransactionManualEntries, saveMasterTransactionManualEntries } from "../services/ManualEntries/manualEntriesServices";
import { getAllParticipants } from "../services/MonthlyCalculations/monthlyServices";
import { getTodayDate } from "../services/Settings/SystemSettingsService";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer, SubSectionTitle } from "../StyledComponents/Container";
import CommonUtilities from "../Utils/CommonUtilities";
import { notifyUser } from "../Utils/notifyUser";
import { getClientDetailsByClientName } from "../Utils/ClientSettingsTable";

type ManualEntriesFormProps = {
    participantField?: boolean;
    accountType?: boolean;
    methodOfPayment?: boolean;
    commentsField?: boolean;
    typeOfTransactions?: boolean;
    dateOfTransactions?: boolean;
    checkReferenceField?: boolean;
    creditLineClose?: boolean;
    amountField?: boolean;
    headingMessage?: string;
    rowDetails?: any;
    transactionTypes?: { name: string; value: string }[];
    pageType?: string;
    navigationPath?: string;

};

const EntryForm: React.FC<ManualEntriesFormProps> = ({
    accountType = true,
    methodOfPayment = true,
    participantField = true,
    creditLineClose = true,
    amountField = true,
    typeOfTransactions = true,
    dateOfTransactions = true,
    commentsField = true,
    checkReferenceField = true,
    headingMessage = "",
    transactionTypes = [],
    rowDetails = {},
    navigationPath = "",
    pageType = "",

}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const translate = t as (key: string) => string;

    const userInfo = useSelector((state: RootState) => state.auth);
    let role: any = userInfo.roles?.roleName;
    const ClientDetails = getClientDetailsByClientName(userInfo?.clientVo?.name);

    const [participants, setParticipants] = useState<any[]>([]);
    const [participantName, setParticipantName] = useState("");
    const [participantId, setParticipantId] = useState<number | null>(null);
    const [openPopUp, setOpenPopUp] = useState(false);
    const [loadingFlag, setLoadingFlag] = useState(false);
    const [sourceList, setSourceList] = useState<any[]>([]);
    const [account, setAccount] = useState<number | string>("");
    const [creditLinePayoff, SetCreditLinePayoff] = useState(false);
    const [transactionType, setTransactionType] = useState(constants.SELECT_PLACEHOLDER);
    const [methodOfPaymentState, setMethodOfPayment] = useState(constants.SELECT_PLACEHOLDER);
    const [minPostingDate, setMinPostingDate] = useState<string | null>(null);
    const [todayDate, setTodayDate] = useState<string | null>(null);
    const [payDate, setPayDate] = useState<string>("");
    const [amount, setAmount] = useState<any>();
    const [checkReference, setCheckReference] = useState("");
    const [comments, setComments] = useState("");
    const AdminProfile = useSelector((state: RootState) => state.participantAdminProfile);
    const methodOfPayments = CommonUtilities.methodOfPayments;

    const [sourceAvailableBalance, setSourceAvailableBalance] = useState<number>(0);

    const [errors, setErrors] = useState<any>({});
    const [isLoading, setIsLoading] = useState(false);
    const [principal, setPrincipal] = useState(0.00);
    const [interest, setInterest] = useState(0.00);
    const [totalAmount, setTotalAmount] = useState(55.00);
    useEffect(() => {
        if (creditLinePayoff) {
            getCreditLineAmountDetailsbyCreditId(rowDetails.creditlineId, userInfo?.clientId, payDate);
        }
    }, [rowDetails?.creditlineId, creditLinePayoff, payDate]);

    async function getCreditLineAmountDetailsbyCreditId(creditId: number, clientId: number, transactionDate?: string) {
        try {
            const response = await getCreditLinePandIAmountbyCreditId(creditId, clientId, transactionDate);
            if (response.status === constants.STATUS_CODES.SUCCESS) {
                const details = response.data.data;
                const interestVal = Number(details?.accruedInterest) || 0;
                const principalVal = Number(details?.outstandingBalance) || 0;
                const interest = interestVal > 0 ? formatCurrency(interestVal) : "";
                const principal = principalVal > 0 ? formatCurrency(principalVal) : "";
                setInterest(interest);
                setPrincipal(principal);
                let total = 0;

                if (interestVal > 0 && principalVal > 0) {
                    total = interestVal + principalVal;
                } else if (interestVal > 0) {
                    total = interestVal;
                } else if (principalVal > 0) {
                    total = principalVal;
                } else {
                    total = 0;
                }

                setAmount(formatCurrency(total));
                if (creditLinePayoff && total === 0) {
                    notifyUser(translate('thereIsNoOutstandingBalanceToPayForTheCreditLinePayment'), 'error', headingMessage);
                }
            } else {
                notifyUser(response?.response?.data?.message, 'error', headingMessage);
                setAmount(0);
            }

        } catch (error: any) {
            notifyUser(error.response?.data?.message || translate("anUnexpectedError"), 'error', headingMessage);
        }
    }
    useEffect(() => {
        fetchDateFromDB();
    }, []);

    useEffect(() => {
        if (todayDate) {
            setPayDate(getLocalToday());
        }
    }, [todayDate]);

    async function fetchDateFromDB() {
        try {
            const response: any = await getTodayDate(constants.ATTRIBUTE_NAME.SETTING_TODAY_SYSDATE_TEST_ENV_ONLY, userInfo.clientId);
            let dateStr: any = response.data.data;
            setTodayDate(dateStr);
        } catch (error) {
            console.error("Error fetching date:", error);
        }
    }

    const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        SetCreditLinePayoff(checked);
        if (event.target.checked) {
        } else {
            setAmount(0);
        }
    };
    const getLocalToday = () => {
        if (!todayDate) return "";
        return todayDate.split("T")[0];
    };
    const isBefore = (d1: string, d2: string) => {
        return new Date(d1) < new Date(d2);
    };

    const validateForm = (): boolean => {
        let newErrors: any = {};

        if ((!participantId) && participantField) {
            newErrors.participant = translate('pleaseSelectTheParticipantName');
        }

        if (accountType && (account === "" || account === null || account === undefined)) {
            newErrors.account = translate('pleaseSelectTheAccount');
        }

        if (typeOfTransactions && (!transactionType || transactionType === constants.SELECT_PLACEHOLDER)) {
            newErrors.transactionType = translate('pleaseSelectTheTransactionType');
        }

        if (methodOfPayment && ((pageType === "invoicesPayments" && transactionType === "VI") ||
            (pageType === "checkingAndSavings" || pageType === "creditLinePayments"))
            && (!methodOfPaymentState || methodOfPaymentState === constants.SELECT_PLACEHOLDER)) {
            newErrors.methodOfPayment = translate('pleaseSelectTheMethodOfPayment');
        }

        if (dateOfTransactions) {
            if (!payDate) {
                newErrors.payDate = translate('paymentDateIsRequired');
                SetCreditLinePayoff(false);
            }
            else if (minPostingDate && isBefore(payDate, minPostingDate)) {
                newErrors.payDate = translate('pleaseSelectTheCorrectDate');
            }
        }

        if (amountField && (!amount || parseNumber(amount) <= 0)) {
            if (creditLinePayoff && pageType === "creditLinePayments") {
                notifyUser(translate('thereIsNoOutstandingBalanceToPayForTheCreditLinePayment'), 'error', headingMessage);
            } else {
                newErrors.amount = translate('pleaseEnterAValidAmount');
            }
        } else if (sourceAvailableBalance && Number(account) > 0 &&
            (transactionType === "DR") &&
            parseNumber(amount) > sourceAvailableBalance
        ) {
            newErrors.amount = translate('amountIsGreaterThanAvailableBalance');
        }

        if ((!comments) && commentsField) {
            newErrors.comments = translate('pleaseEnterTheComments');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleParticipantChange = (event: any, value: any) => {
        if (value) {
            setParticipantName(value.participantName);
            setParticipantId(value.participantId);
            fetchAccounts(value.participantId);
            setErrors({ ...errors, participant: "" });
        }
    };

    async function getAllParticipantsList() {
        setIsLoading(true);
        try {
            const ParticipantVo = { participantName: "", isActive: constants.IS_ACTIVE_YES, clientId: userInfo.clientId };
            const responseList = await getAllParticipants(ParticipantVo);
            if (responseList.status === constants.STATUS_CODES.SUCCESS) {
                const participants = responseList.data.data.map((p: any) => ({
                    participantId: p.participantId,
                    participantName: p.participantName,
                }));
                setParticipants(participants);
            } else {
                setParticipants([]);
            }
        } catch (error) {
            console.error(translate('errorFetchingParticipantsList'), error);
        } finally {
            setIsLoading(false);
        }
    }

    const fetchAccounts = async (pId: number) => {
        try {
            const result = await getAllFundSource(pId.toString(), userInfo.clientId);
            if (result?.status === constants.STATUS_CODES.SUCCESS) {
                const { cashAccounts, funds } = result.data?.data || {};
                const totalFundArray = [
                    ...(cashAccounts?.fundName
                        ? [{
                            source: cashAccounts.fundName,
                            availableBalance: cashAccounts.availableBalance,
                            fundId: cashAccounts.fundId,
                            isActive: null,
                        }]
                        : []),
                    ...funds
                        .filter((fund: any) => fund.isActive !== constants.IS_ACTIVE_NO)
                        .map((fund: any) => ({
                            source: fund.fundName,
                            availableBalance: fund.availableBalance,
                            fundId: fund.fundId,
                            isActive: fund.isActive,
                        })),
                ];
                setSourceList(totalFundArray);
                return totalFundArray;
            } else {
                notifyUser(result?.data?.message || translate("failedToFetchFundSources"), "error", headingMessage);
            }
        } catch (error: any) {
            notifyUser(error.response?.data?.message || translate("failedToFetchFundSources:"), "error", headingMessage);

        }
    };

    const handleAccountChange = (event: any, value: any) => {
        setAccount(value?.fundId ?? "");
        const balance = value?.availableBalance || 0;
        setSourceAvailableBalance(balance);
        setErrors({ ...errors, account: "" });
        if (sourceAvailableBalance && Number(value?.fundId) > 0 &&
            (transactionType === "DR") &&
            parseNumber(amount) > sourceAvailableBalance
        ) {
            setErrors({ ...errors, amount: translate('amountIsGreaterThanAvailableBalance'), account: "" });
        } else {
            setErrors({ ...errors, amount: "", account: "" });
        }
    };

    const handlePopUpClose = () => setOpenPopUp(false);
    const handleCancel = () => setOpenPopUp(true);
    function handleConfirm() { navigate(navigationPath); }

    const getFormattedPostingDate = (dateStr: string) => {
        const now = new Date();
        const [year, month, day] = dateStr.split("-");
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };

    const parseNumber = (value: any) => {
        if (typeof value === 'string') {
            return Number(value.replace(/[^0-9.-]+/g, '')) || 0;
        }
        return typeof value === 'number' ? value : 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        const masterTransactionsManualVo = {
            participantId,
            amount: parseNumber(amount),
            paymentMethod: transactionType === "DI" ? null : methodOfPaymentState,
            clientId: userInfo.clientId,
            ...(pageType === "invoicesPayments"
                ? {
                    sourceId: account,
                    postingDate: getFormattedPostingDate(payDate),
                    type: transactionType,
                    comments: comments,
                    documentNumber: transactionType === "DI" ? null : checkReference,
                    isActive: constants.IS_ACTIVE_YES,
                }
                : pageType === "creditLinePayments"
                    ? {
                        creditLineId: rowDetails?.creditlineId,
                        postingDate: getFormattedPostingDate(payDate),
                        documentNumber: checkReference,
                        description: comments,
                        transactionType: transactionType,
                        isCreditLinePayOff: creditLinePayoff ? constants.IS_ACTIVE_YES : constants.IS_ACTIVE_NO,
                    }
                    : account === 0
                        ? {
                            postingDate: getFormattedPostingDate(payDate),
                            masterTransactionType: transactionType,
                            transactionDesc: comments,
                            transactionCheckReference: checkReference,
                            includeExternalIntegration: constants.IS_ACTIVE_YES,
                        }
                        : {
                            sourceAccount: account,
                            comments: comments,
                            transactionPostingDate: getFormattedPostingDate(payDate),
                            transactionType,
                            transactionCheckReference: checkReference,
                            includeExternalIntegration: constants.IS_ACTIVE_YES,
                        }),
        };

        try {
            setLoadingFlag(true);
            if (pageType === "invoicesPayments") {
                const response = await saveInvoiceTransactionManualEntries(masterTransactionsManualVo);
                if (response.status === constants.STATUS_CODES.SUCCESS || response.status === constants.STATUS_CODES.CREATED) {
                    notifyUser(response?.data?.message, 'success', headingMessage);
                    navigate(navigationPath);
                }
                else {
                    notifyUser(response?.data?.message, 'error', headingMessage);
                }
            } else if (pageType === "creditLinePayments") {
                if (amount && parseNumber(amount) > 0) {
                    const response = await saveCreditlineTransactionManualEntries(masterTransactionsManualVo);
                    if (response.status === constants.STATUS_CODES.SUCCESS || response.status === constants.STATUS_CODES.CREATED) {
                        notifyUser(response?.data?.message, 'success', headingMessage);
                        navigate(navigationPath);
                    }
                    else {
                        notifyUser(response?.data?.message, 'error', headingMessage);
                    }
                }

            } else {
                if (account === 0) {
                    const response = await saveMasterTransactionManualEntries(masterTransactionsManualVo);
                    if (response.status === constants.STATUS_CODES.SUCCESS || response.status === constants.STATUS_CODES.CREATED) {
                        notifyUser(response?.data?.message, 'success', headingMessage);
                        navigate(navigationPath);
                    }
                    else {
                        notifyUser(response?.data?.message, 'error', headingMessage);
                    }
                } else {
                    const response = await saveFundTransactionManualEntries(masterTransactionsManualVo);
                    if (response.status === constants.STATUS_CODES.SUCCESS || response.status === constants.STATUS_CODES.CREATED) {
                        notifyUser(response?.data?.message, 'success', headingMessage);
                        navigate(navigationPath);
                    }
                    else {
                        notifyUser(response?.data?.message, 'error', headingMessage);
                    }
                }
            }
        } catch (error: any) {
            notifyUser(error?.response?.data?.message || translate("errorSavingManualTransaction"), 'error', headingMessage);
        } finally {
            setLoadingFlag(false);
        }
    };

    useEffect(() => {
        if (!minPostingDate || !payDate) return;
        if (isBefore(payDate, minPostingDate)) {
            setErrors((prev: any) => ({
                ...prev,
                payDate: translate('pleaseSelectTheCorrectDate'),
            }));
        } else {
            setErrors((prev: any) => ({
                ...prev,
                payDate: "",
            }));
        }
    }, [minPostingDate, payDate]);

    useEffect(() => {
        getAllParticipantsList();
        if (AdminProfile.participantAdminProfile !== constants.ADMIN_VIEW) {
            setParticipantId(userInfo.defaultParticipantId);
            if (pageType === "checkingAndSavings" || pageType === "invoicesPayments") {
                fetchAccounts(userInfo.defaultParticipantId);
            }
        }
    }, []);
    useEffect(() => {
        if (pageType === "creditLinePayments" && rowDetails?.creditlineId) {
            getCreditlineDate();
        }
        if ((pageType === "checkingAndSavings" || pageType === "invoicesPayments") && account) {
            getFundDate();
        }
    }, [pageType, account, rowDetails?.creditlineId]);
    async function getCreditlineDate() {
        setLoadingFlag(true);
        try {
            const response = await getCreditlineTransactionDate(rowDetails?.creditlineId, userInfo.clientId);
            if (response.status === constants.STATUS_CODES.SUCCESS || response.status === constants.STATUS_CODES.CREATED) {
                const lowerBound = response?.data?.data?.postingDateLowerBound;
                if (lowerBound) {
                    setMinPostingDate(lowerBound);
                }
            }
            else {
                notifyUser(response?.data?.message, 'error', headingMessage);
            }
        } catch (error: any) {
            notifyUser(error?.response?.data?.message || "Error fetching data:", 'error', headingMessage);
        } finally {
            setLoadingFlag(false);
        }
    }
    async function getFundDate() {
        setLoadingFlag(true);
        try {
            const response = await getFundTransactionDate(Number(account), userInfo.clientId);
            if (response.status === constants.STATUS_CODES.SUCCESS || response.status === constants.STATUS_CODES.CREATED) {
                const lowerBound = response?.data?.data?.postingDateLowerBound;
                if (lowerBound) {
                    setMinPostingDate(lowerBound);
                }
            }
            else {
                notifyUser(response?.data?.message, 'error', headingMessage);
            }
        } catch (error: any) {
            notifyUser(error?.response?.data?.message || "Error fetching data:", 'error', headingMessage);
        } finally {
            setLoadingFlag(false);
        }
    }

    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{headingMessage} {pageType === "loanPayments" && `: ${rowDetails?.loanName}`}  {pageType === "creditLinePayments" && `: ${rowDetails?.name}`}</SectionTitle>
                    <Box sx={{ mt: 3, ml: 2 }}>
                        <Grid container spacing={2}>
                            <Grid container spacing={2} size={4} direction="column">
                                {participantField && AdminProfile.participantAdminProfile === constants.ADMIN_VIEW ? (
                                    <Grid size={{ xs: 12, sm: 6, lg: 11 }}>
                                        <CustomAutocomplete
                                            labelText={translate('parishCustomer')}
                                            required
                                            options={participants}
                                            value={participants.find((p) => p.participantId === participantId) || null}
                                            onChange={handleParticipantChange}
                                            getOptionLabel={(option) => option.participantName}
                                            isOptionEqualToValue={(option, value) => option.participantId === value?.participantId}
                                            placeholder="Select Participant"
                                            error={!!errors.participant}
                                        />
                                        {errors.participant && (
                                            <FormHelperText error>{errors.participant}</FormHelperText>
                                        )}
                                    </Grid>
                                ) : (
                                    <Grid size={{ xs: 12, sm: 6, lg: 11 }}>
                                        <CustomTextField
                                            readOnly
                                            labelText={translate('parishCustomer')}
                                            value={
                                                participants.find((p) => p.participantId === participantId)?.participantName || ''
                                            }
                                            fullWidth
                                            style={{ marginBottom: "1px" }}
                                        />
                                    </Grid>
                                )}

                                {accountType && (
                                    <Grid size={{ xs: 12, sm: 6, lg: 11 }}>
                                        <CustomAutocomplete
                                            labelText={translate('account')}
                                            required
                                            options={sourceList}
                                            value={sourceList.find((s) => String(s.fundId) === String(account)) || null}
                                            onChange={handleAccountChange}
                                            getOptionLabel={(option) =>
                                                `${option.source} : $${(option.availableBalance || 0).toFixed(2)}`
                                            }
                                            isOptionEqualToValue={(option, value) => option.fundId === value?.fundId}
                                            placeholder="Select Account"
                                            error={!!errors.account}
                                        />
                                        {errors.account && (
                                            <FormHelperText error>{errors.account}</FormHelperText>
                                        )}
                                    </Grid>
                                )}

                                {typeOfTransactions && (<Grid size={{ xs: 12, sm: 6, lg: 11 }}>
                                    <CustomAutocomplete
                                        labelText={translate('typeofTransaction')}
                                        required
                                        options={transactionTypes.filter((t) => t.value)}
                                        value={transactionTypes.find((t) => t.value === transactionType) || transactionTypes[0]}
                                        onChange={(e, val) => {
                                            setTransactionType(val?.value || "");
                                            if (val?.value !== "CR") {
                                                SetCreditLinePayoff(false);
                                            }
                                            setErrors({ ...errors, transactionType: "" });
                                            if (sourceAvailableBalance && Number(account) > 0 &&
                                                (val?.value === "DR") &&
                                                parseNumber(amount) > sourceAvailableBalance
                                            ) {
                                                setErrors({ ...errors, amount: translate('amountIsGreaterThanAvailableBalance'), transactionType: "" });
                                            } else {
                                                setErrors({ ...errors, amount: "", transactionType: "" });
                                            }
                                        }}
                                        getOptionLabel={(option) => option.name}
                                        isOptionEqualToValue={(option, value) => option.value === value?.value}
                                        placeholder={constants.SELECT_PLACEHOLDER}
                                        error={!!errors.transactionType}
                                    />
                                    {errors.transactionType && (
                                        <FormHelperText error>{errors.transactionType}</FormHelperText>
                                    )}
                                </Grid>)}
                                {dateOfTransactions && (<Grid size={{ xs: 12, sm: 6, lg: 11 }}>
                                    <CustomTextField
                                        type="date"
                                        labelText={translate('dateofTransaction')}
                                        required
                                        value={payDate}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setPayDate(value);
                                            setErrors((prev: any) => {
                                                const newErrors = { ...prev };
                                                if (!value) {
                                                    newErrors.payDate = translate('paymentDateIsRequired');
                                                    SetCreditLinePayoff(false);
                                                } else if (minPostingDate && isBefore(value, minPostingDate)) {
                                                    newErrors.payDate = translate('pleaseSelectTheCorrectDate');
                                                } else {
                                                    newErrors.payDate = "";
                                                }
                                                return newErrors;
                                            });
                                        }}
                                        error={!!errors.payDate}
                                        inputProps={{
                                            max: getLocalToday(),
                                            ...(minPostingDate && {
                                                min: minPostingDate
                                            })
                                        }}
                                        style={{ marginBottom: "1px" }}
                                    />
                                    {errors.payDate && (
                                        <FormHelperText error>{errors.payDate}</FormHelperText>
                                    )}
                                </Grid>)}
                                {creditLineClose && transactionType === "CR" && (<Grid size={{ xs: 12, sm: 6, lg: 11 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={creditLinePayoff}
                                                onChange={handleCheckboxChange}
                                                disabled={errors.payDate ? true : false}
                                            />
                                        }
                                        label={translate('creditLinePayoff')}
                                        labelPlacement="end"
                                    />
                                </Grid>)}

                                {amountField && (<Grid size={{ xs: 12, sm: 6, lg: 11 }}>
                                    <NumericFormat
                                        customInput={CustomTextField}
                                        labelText={translate('amount')}
                                        required
                                        error={!!errors.amount}
                                        value={amount}
                                        readOnly={creditLinePayoff}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setAmount(value);
                                            const newErrors = { ...errors };
                                            if (!value || parseNumber(value) <= 0) {
                                                newErrors.amount = translate('pleaseEnterAValidAmount');
                                            } else if (sourceAvailableBalance && Number(account) > 0 &&
                                                (transactionType === "DR") &&
                                                parseNumber(value) > sourceAvailableBalance
                                            ) {
                                                setErrors({ ...errors, amount: translate('amountIsGreaterThanAvailableBalance') });
                                            } else {
                                                setErrors({ ...errors, amount: "" });
                                            }
                                        }}
                                        placeholder='0.00'
                                        isAllowed={(values) => {
                                            const { floatValue } = values;
                                            return (
                                                floatValue === undefined ||
                                                floatValue <= constants.MAX_AMOUNT_ALLOWED
                                            );
                                        }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                                        }}
                                        sx={{ marginLeft: 0.5, pl: 0 }}
                                        thousandSeparator
                                        decimalScale={2}
                                        fixedDecimalScale={false}
                                        allowNegative={true}
                                    />
                                    {errors.amount && (
                                        <FormHelperText error sx={{ mt: -1.1 }}>{errors.amount}</FormHelperText>
                                    )}
                                </Grid>)}
                            </Grid>
                            <Grid container spacing={2} size={4} direction="column">
                                {methodOfPayment && ((pageType === "invoicesPayments" &&
                                    transactionType === "VI") ||
                                    (pageType === "checkingAndSavings" || pageType === "creditLinePayments")) && (
                                        <Grid size={{ xs: 12, sm: 6, lg: 11 }}>
                                            <CustomAutocomplete
                                                labelText={translate('methodofPayment')}
                                                required
                                                options={methodOfPayments.filter((m) => m.value)}
                                                value={methodOfPayments.find((m) => m.value === methodOfPaymentState) || methodOfPayments[0]}
                                                onChange={(e, val) => {
                                                    setMethodOfPayment(val?.value || "");
                                                    setErrors({ ...errors, methodOfPayment: "" });
                                                }}
                                                getOptionLabel={(option) => option.name}
                                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                                placeholder={constants.SELECT_PLACEHOLDER}
                                                error={!!errors.methodOfPayment}
                                            />
                                            {errors.methodOfPayment && (
                                                <FormHelperText error>{errors.methodOfPayment}</FormHelperText>
                                            )}
                                        </Grid>
                                    )}
                                {checkReferenceField && ((pageType === "invoicesPayments" &&
                                    transactionType === "VI") ||
                                    (pageType === "checkingAndSavings" || pageType === "creditLinePayments")) && (
                                        <Grid size={{ xs: 12, sm: 6, lg: 11 }}>
                                            <CustomTextField
                                                labelText={translate('checkReference')}
                                                value={checkReference}
                                                onChange={(e) => {
                                                    const raw = e.target.value || "";
                                                    const trimmedValue = raw.replace(/^\s+/, "");
                                                    setCheckReference(trimmedValue);
                                                }}
                                                style={{ marginBottom: "1px" }}
                                                inputProps={{
                                                    maxLength: 20,
                                                }}
                                            />
                                        </Grid>)}
                                {commentsField && (<Grid size={{ xs: 12, sm: 6, lg: 11 }}>
                                    <CustomTextField
                                        labelText={translate('enterComments')}
                                        required
                                        multiline
                                        minRows={3}
                                        maxRows={5}
                                        value={comments}
                                        inputProps={{
                                            maxLength: 255,
                                        }}
                                        onChange={(e) => {
                                            const raw = e.target.value || "";
                                            const trimmedValue = raw.replace(/^\s+/, "");
                                            setComments(trimmedValue);
                                            const newErrors = { ...errors };
                                            if (!trimmedValue) {
                                                newErrors.comments = translate('pleaseEnterTheComments');
                                            } else {
                                                newErrors.comments = "";
                                            }
                                            setErrors(newErrors);
                                        }}
                                        error={!!errors.comments}
                                        style={{ marginBottom: "1px" }}
                                    />
                                    {errors.comments && (
                                        <FormHelperText error>{errors.comments}</FormHelperText>
                                    )}
                                </Grid>)}
                            </Grid>
                            <Grid container spacing={2} size={4} direction="column">
                            </Grid>
                        </Grid>
                        <Box className={basestyle.buttoncontainer} sx={{ mt: 3 }}>
                            <ContainedButton onClick={handleSave} disabled={ClientDetails?.name === constants.CLIENT_NAME_DC_GARY && [constants.ROLES_READ_ONLY_USER].includes(role)}>{translate('save')}</ContainedButton>
                            <OutlinedButton onClick={handleCancel}> {translate('cancel')} </OutlinedButton>
                        </Box>
                    </Box>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
            <CancelPopUp
                open={openPopUp}
                handleClose={handlePopUpClose}
                saveChanges={() => handleConfirm()}
                popupTitle={translate('confirmCancel')}
                message={translate('doYouWantToCancelChanges?')}
            />
            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    display: "flex",
                    flexDirection: "column",
                }}
                open={loadingFlag}
            >
                <CircularProgress color="inherit" />
                <SubSectionTitle >{translate('pleasewait')}</SubSectionTitle>
            </Backdrop>
        </StyledParentContainer>
    );
};

export default EntryForm;
